// Typed wrappers for Insights v2 admin analytics + Ask AI edge function.
import { supabase } from '@/lib/supabase';

export type AnchorTrendPoint = {
  anchor_id: string;
  name: string;
  week_start: string;
  n: number;
  mean: number | null;
  suppressed: boolean;
};

export async function getAnchorTrendWeekly(surveyId: string, weeks = 12): Promise<AnchorTrendPoint[]> {
  const { data, error } = await supabase.rpc('admin_anchor_trend_weekly', {
    _survey_id: surveyId,
    _weeks: weeks,
  });
  if (error) throw error;
  return (data ?? []) as AnchorTrendPoint[];
}

export type AnchorMover = {
  anchor_id: string;
  name: string;
  current_mean: number | null;
  prior_mean: number | null;
  delta: number | null;
  n_current: number;
  n_prior: number;
  suppressed?: boolean;
};

export async function getAnchorBiggestMovers(surveyId: string, windowDays = 30): Promise<AnchorMover[]> {
  const { data, error } = await supabase.rpc('admin_anchor_biggest_movers', {
    _survey_id: surveyId,
    _window_days: windowDays,
  });
  if (error) throw error;
  return (data ?? []) as AnchorMover[];
}

export type SegmentCutCell = {
  anchor_id: string;
  anchor_name: string;
  band_id: string;
  band_label: string;
  dimension: string;
  n: number;
  mean: number | null;
  suppressed: boolean;
};

export async function getAnchorSegmentCuts(surveyId: string): Promise<SegmentCutCell[]> {
  const { data, error } = await supabase.rpc('admin_anchor_segment_cuts', { _survey_id: surveyId });
  if (error) throw error;
  return (data ?? []) as SegmentCutCell[];
}

export type HeatmapCell = {
  week_start: string;
  dow: number;
  n: number;
  mean: number | null;
  suppressed: boolean;
};

export async function getPulseHeatmap(orgId: string, weeks = 12): Promise<HeatmapCell[]> {
  const { data, error } = await supabase.rpc('admin_pulse_heatmap', { _org_id: orgId, _weeks: weeks });
  if (error) throw error;
  return (data ?? []) as HeatmapCell[];
}

export type OrgMover = {
  anchor_id: string;
  anchor_name: string;
  survey_id: string;
  survey_name: string;
  current_mean: number | null;
  prior_mean: number | null;
  delta: number | null;
  n_current: number;
  n_prior: number;
};

export async function getOrgBiggestMovers(orgId: string, windowDays = 30): Promise<OrgMover[]> {
  const { data, error } = await supabase.rpc('admin_org_biggest_movers', {
    _org_id: orgId,
    _window_days: windowDays,
  });
  if (error) throw error;
  return (data ?? []) as OrgMover[];
}

export type AskChart = {
  type: 'bar' | 'line' | 'table';
  title: string;
  x_label?: string;
  y_label?: string;
  series: Array<{ label: string; value: number }>;
};

export type AskResult = {
  answer_markdown: string;
  chart: AskChart | null;
  citations: string[];
  insufficient_data: boolean;
  generated_at: string;
  error?: string;
};

export async function askInsights(args: {
  orgId: string;
  surveyId?: string;
  question: string;
}): Promise<AskResult> {
  const { data, error } = await supabase.functions.invoke('admin-ask-insights', {
    body: {
      org_id: args.orgId,
      survey_id: args.surveyId ?? null,
      question: args.question,
    },
  });
  if (error) throw error;
  return data as AskResult;
}
